import React, { useRef, useEffect, useState } from 'react';
import { Heart, Users, Church, ArrowRight } from 'lucide-react';
import { motion, useScroll, AnimatePresence } from 'framer-motion';
import PageLayout from './layout/PageLayout';
import CTASection from './shared/CTASection';
import supportMessages from '../data/supportMessages.jsx';
import ServiceSection from './shared/ServicesSection';

const HomePage = () => {
  const containerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex(current => (current + 1) % supportMessages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <PageLayout showHero={false}>
      <div ref={containerRef} className="bg-bg-primary">
        <section className="relative min-h-[80vh] flex items-center pt-12">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto text-center">
              <motion.div 
                className="flex flex-col items-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold leading-tight">
                  A Safe Haven for
                </h1>
                
                {/* Animated Support Messages */}
                <div className="my-2 sm:my-4 relative w-full">
                  <AnimatePresence initial={false} mode="wait">
                    <motion.div
                      key={currentIndex}
                      className="w-full"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.7, delay: 0.1 }}
                    >
                      <h2 className="text-size-dynamic font-semibold text-gradient leading-tight py-2 mb-0 break-words">
                        {supportMessages[currentIndex]}
                      </h2>
                    </motion.div>
                  </AnimatePresence>
                </div>

                <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold leading-tight mt-0 sm:mt-2 md:mt-4">
                  in Our Community
                </h1>

                <motion.p
                  className="text-xl text-text-secondary max-w-2xl mt-8 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                >
                  Through our Neverland Initiative, we provide support, counseling, and community for youth aged 11-30.
                </motion.p>

                <motion.div
                  className="flex flex-col sm:flex-row gap-6 items-center mt-12"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                >
                  <motion.a
                    href="/contact"
                    className="cta-primary-button flex items-center"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Get Support
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </motion.a>
                  <motion.a
                    href="/donate"
                    className="cta-secondary-button flex items-center"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Support Our Mission
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </motion.a>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <ServiceSection 
          services={[
            {
              icon: Heart,
              title: "Neverland Initiative",
              description: "Support for youth aged 11-30",
              gradient: "from-purple-600 to-blue-600"
            },
            {
              icon: Users,
              title: "Counseling Services",
              description: "Professional support and guidance",
              gradient: "from-blue-600 to-cyan-500"
            },
            {
              icon: Church,
              title: "Spiritual Community", 
              description: "Inclusive circle services",
              gradient: "from-cyan-500 to-green-500"
            }
          ]}
          variant="primary"
          className="py-24"
        />

        {/* Mission Statement Section */}
        <section className="bg-bg-secondary py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-semibold mb-6">Our Mission</h2>
              <p className="text-lg text-text-secondary">
              As a 501(c)(3) nonprofit organization, we are dedicated to fostering spiritual, academic, and personal growth in youth who have faced rejection or adversity. Through our inclusive community, we create a sanctuary where diverse traditions are honored and celebrated. Our commitment extends beyond providing safe haven – we empower each individual to shape their own path, discover their inner strength, and contribute to our vibrant spiritual community.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection
          title="Ready to join our community?"
          description="Whether you need support or want to help others, we welcome you."
          primaryButtonText="Contact Us"
          primaryButtonLink="/contact"
          secondaryButtonText="Learn More"
          secondaryButtonLink="/about"
        />
      </div>
    </PageLayout>
  );
};

export default HomePage;